import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { rootStore } from "@/mobx/store";
import styled from "styled-components";
import { observer } from "mobx-react";
import { useNavigate } from "react-router-dom";

import fonts from "@/design/typography";
import colors from "@/design/colors";
import { Page_settings } from "@/config/page_settings";
import { ChevronLeft, ChevronRight } from "@/components/icons/chevron";
import Button from "@/components/button";
import ResetIcon from "@/components/icons/reset";
import ConfirmModal from "@/components/common/ConfirmModal";
import { paymentLink, paymentLinkQuery } from "../paymentLink";

/* **********************************
 * TYPES
 * 타입 정의
 ********************************** */
type PaymentMethod = "CARD" | "NAVER_PAY" | "KAKAO_PAY" | null;

/* **********************************
 * CONSTANTS
 * 상수 정의
 ********************************** */

const PaymentProcessPage = observer(() => {
  /* **********************************
   * GLOBAL STATE
   * 전역 상태 및 전역 변수
   ********************************** */
  const info = rootStore.getPaymentInfo;

  /* **********************************
   * LOCAL STATE
   * 컴포넌트 내부에서 사용하는 상태
   ********************************** */
  const navigate = useNavigate();
  const context: any = useContext(Page_settings);

  // 결제 타입
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(null);
  const [approvalAmount, setApprovalAmount] = useState("0");
  const [resultMsg, setResultMsg] = useState("");
  

  /* **********************************
   * USE EFFECT
   * 컴포넌트 렌더링 시 실행되는 함수
   ********************************** */
  //결제서비스 앱카드 호출
  const getPaymentInfo = () => {
    //결제정보요청  
    //const info = rootStore.getPaymentInfo;
    //alert(info.txId);
    const { txId, accessToken } = paymentLink();
            
    context.post(
      `/payment/requestPaymentInfo`,
      {
        txId: txId,
        accessToken: accessToken,
      },
      (response) => {
        if(!response || !response.processStatus){
          alert("결제 정보를 불러올 수 없습니다.");
          return;
        }
        if(response.processStatus === "APPROVE_COMPLETE" || response.processStatus === "CANCEL_COMPLETE"){
          navigate("/payments/result");
        }else if(response.processStatus === "STORE_DELETE"){
          alert("삭제 된 결제요청입니다.");
          navigate("/payments/result");
        }else{
          setPaymentData(response);
        }
      }
    );
  };
  const setPaymentData = (data) => {
    setApprovalAmount(data.approvalAmount.toLocaleString());
    setResultMsg(data.resultMsg);
        
  };

  useEffect(() => {
    const info = rootStore.getPaymentInfo;

    console.table({
      method: info?.method,
      cardCompany: info?.cardCompany,
      installment: info?.installment,
      signature: info?.signature,
    });

    setPaymentMethod(info?.method ?? null);

    getPaymentInfo();
  }, []);

  /* **********************************
   * HANDLERS
   * 사용자 입력 및 이벤트 처리
   ********************************** */
  // 결제 실패 확인 모달
  const handleConfirm = async () => {
    navigate("/payments?" + paymentLinkQuery(paymentLink()));
  };

  return (
    <Container>
      <Wrapper>
        {/* 헤더 */}
        <TopBar>
          <BackButton onClick={() => navigate(-1)}>
            <ChevronLeft width="28px" height="28px" />
          </BackButton>
          <TitleText>결제하기</TitleText>
          <Spacer /> {/* 가운데 정렬용 */}
        </TopBar>

        <ConfirmModal
          width="340px"
          height="225px"
          padding="32px 24px 24px 24px"
          isOpen={true}
          onClose={() => {}}
          messageMargin="0 0 24px 0" // 모달 내부 메시지 영역 margin 옵션
          message={
            <ModalContent>
              <ErrorIcon>!</ErrorIcon>
              <AmountText>{approvalAmount}원 결제실패</AmountText>
              <Description>{resultMsg}</Description>
            </ModalContent>
          }
          buttons={[
            {
              text: "다른 수단으로 결제하기",
              onClick: handleConfirm,
              buttonColor: colors.blue600,
              textColor: colors.white,
              width: "284px",
              height: "48px",
              fontStyle: {
                fontSize: "15px",
                fontWeight: 500,
                ...fonts.body2Medium,
              },
            },
          ]}
        />
      </Wrapper>
    </Container>
  );
});

export default PaymentProcessPage;

// 페이지 전체
const Container = styled.div`
  max-width: 600px;
  width: 100%;
  height: 100%;
  background-color: ${colors.gray50};
  box-sizing: border-box;
  margin: 0 auto;
  overflow-y: auto;
`;

// 페이지 전체 > 래퍼
const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${colors.gray50};
`;

const TopBar = styled.div`
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${colors.gray50};
  padding: 0 20px;
`;

const BackButton = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const TitleText = styled.div`
  flex: 1;
  text-align: center;
  font-family: ${fonts.body1SemiBold.fontFamily};
  font-size: ${fonts.body1SemiBold.fontSize};
  line-height: ${fonts.body1SemiBold.lineHeight};
  color: ${colors.gray900};
`;

const Spacer = styled.div`
  width: 28px; /* BackButton과 같은 너비 */
`;

// 모달
const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
`;

const ErrorIcon = styled.div`
  width: 32px;
  height: 32px;
  background-color: ${colors.warning};
  color: white;
  font-size: 20px;
  font-weight: bold;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AmountText = styled.div`
  font-family: ${fonts.body1SemiBold.fontFamily};
  font-size: ${fonts.body1SemiBold.fontSize};
  line-height: ${fonts.body1SemiBold.lineHeight};
  font-weight: 600;
  color: ${colors.gray900};
`;

const Description = styled.div`
  font-family: ${fonts.detail2Medium.fontFamily};
  font-size: ${fonts.detail2Medium.fontSize};
  line-height: ${fonts.detail2Medium.lineHeight};
  color: ${colors.gray600};
  font-weight: 500;
`;
